import { useEffect, useState } from "react";
import type { PollOptions } from "@/api/client";

type Poll<T> = (id: string, options: PollOptions<T>) => Promise<T>;

interface PollState<T> {
  run: string;
  data: T | null;
  error: Error | null;
}

/**
 * Follow a background job (an identification, a recording) until it leaves `processing`,
 * re-rendering with every snapshot. `retry` starts over after a failure; snapshots of another
 * id are never shown, so navigating between jobs never flashes the previous one.
 */
export function usePolling<T extends { id: string }>(id: string, poll: Poll<T>) {
  const [attempt, setAttempt] = useState(0);
  const run = `${attempt}:${id}`;
  const [state, setState] = useState<PollState<T>>({ run, data: null, error: null });
  useEffect(() => {
    const controller = new AbortController();
    poll(id, {
      signal: controller.signal,
      onUpdate: (data) => setState({ run, data, error: null }),
    }).catch((err: unknown) => {
      if (controller.signal.aborted) return;
      const error = err instanceof Error ? err : new Error(String(err));
      setState((s) => ({ run, data: s.run === run ? s.data : null, error }));
    });
    return () => controller.abort();
  }, [id, run, poll]);
  const current = state.run === run ? state : { ...state, error: null };
  return {
    data: current.data?.id === id ? current.data : null,
    error: current.error,
    retry: () => setAttempt((n) => n + 1),
  };
}
